import { PierGroup as ServerPierGroup } from 'server/models/PierGroup';
import { Berth } from 'data/models/Berth';
import { BerthClosure } from 'data/models/BerthClosure';
import { BerthReservation } from 'data/models/BerthReservation';
import { Measurement } from 'data/models/Measurement';
import { UnlistedBerthReservation } from 'data/models/UnlistedBerthReservation';

export { PierGroupData, PierGroupOptions } from 'server/models/PierGroup';

export class PierGroup extends ServerPierGroup {
    Reservations() {
        let bs = this.Berths(),
            reses = [] as BerthReservation[];
        for (let b of bs) {
            reses = reses.concat(b.Reservations());
        }
        return reses;
    }

    UnlistedReservations() {
        let bs = this.Berths(),
            unlistedReservations = [] as UnlistedBerthReservation[];
        for (let b of bs) {
            unlistedReservations = unlistedReservations.concat(b.UnlistedReservations());
        }
        return unlistedReservations;
    }

    BerthClosures() {
        let bs = this.Berths(),
            closures = [] as BerthClosure[];
        for (let b of bs) {
            closures = closures.concat(b.Closures());
        }
        return closures;
    }

    /**
     * Determine the berth from this piergroup that is most utilized by the given span of
     * pier.
     * @param footmark The starting footmark of the span of pier.
     * @param length The length of the span of pier.
     */
    // NOTE: We also implement this logic in C#. See PortCall.Models\Port\PierGroup.cs. Make sure to keep the
    // two implementations in sync.
    MostUtilizedBerth(footmark : Measurement, length : Measurement) {
        if (length.Num() < 0) throw new Error("Length must be nonnegative");

        // Do all computations in terms of this PierGroup's Length UOM
        const
            uom = this.Length.UOM(),
            spanStart = footmark.convert(uom).Num(),
            spanLength = length.convert(uom).Num(),
            spanEnd = spanStart + spanLength;

        // Traverse in StartMark order to give a deterministic winner in case of ties (first berth in StartMark order wins).
        // Also, filter out zero-length berths (degenerate case), as they would give a denominator of zero below.
        const berths = this.Berths().filter(b => b.Length.Num() > 0);
        berths.sort(Berth.StartMarkOrder);

        let winner : Berth | null = null;
        // Actually, any negative value will do b/c the loop below always produces fractionOccupied >= 0.
        let maxFractionOccupied = -Infinity;

        for (let i = 0; i < berths.length; i++) {
            // The general idea is that we say the most utilized berth is the one that has the highest fraction of
            // berth length occupied.
            const
                berthStart = berths[i].StartMark.convert(uom).Num(),
                berthLength = berths[i].Length.convert(uom).Num(),
                berthEnd = berthStart + berthLength;
            // spanLength           -- smallest of the 4 when span is contained completely inside of berth
            // berthLength          -- smallest of the 4 when berth is contained completely inside of span
            // berthEnd - spanStart -- smallest of the 4 when span intersects end of berth but not start
            // spanEnd - berthStart -- smallest of the 4 when span intersects start of berth but not end
            // Taking max with 0    -- Min of 4 values above will be negative when span and berth don't intersect. Count that as 0.
            const
                lengthOccupied = Math.max(0, Math.min(spanLength, berthLength, berthEnd - spanStart, spanEnd - berthStart)),
                fractionOccupied = lengthOccupied / berthLength;
            if (fractionOccupied > maxFractionOccupied) {
                // Normal case. Found a berth w/ a larger fraction occupied. That's our current best berth.
                winner = berths[i];
                maxFractionOccupied = fractionOccupied;
            } else if (i === berths.length - 1 && berthEnd <= spanStart) {
                // Special case. If the provided footmark is beyond the end of the piergroup, then all berths will have a
                // fraction occupied of 0 and our normal first-berth-wins-in-case-of-ties logic will select the first berth
                // as the winner. But we wanna say the last berth is actually the winner in that case.
                winner = berths[i];
            } else if (spanLength === 0 && berthStart <= spanStart && spanStart < berthEnd) {
                // Degenerate case. If provided length is 0, then all berths will have a fraction occupied of 0. In that case,
                // say the winner is whichever berth contains the provided starting footmark (which will also be the ending
                // footmark since length is 0).
                winner = berths[i];
            }
        }

        // Another degenerate case. The only way winner can be null at this point is if berths is empty. If that's b/c we
        // filtered out zero-length berths above and no other berths were left, then just declare the first berth the winner.
        if (winner === null && this.Berths().length > 0) winner = this.Berths()[0];

        return winner;
    }
}



// WEBPACK FOOTER //
// ./data/models/PierGroup.ts